/**
 * The failure report (BENCH-V3-PLAN §7.2, DESIGN ED-93): for every kept and every failed unit, what the chosen version did
 * and how it fell short, built from the version's `AttemptEval` and its meaning checks. Pure and bounded: it keeps no text of
 * other versions, at most three items in each list, spans cut to 60 characters, and a one-sentence summary of at most 40
 * words. The loop stores it in the attempt's record when the attempt is made and in the unit's state when the unit is
 * resolved; the lens reads it from the state.
 *
 * Severity, worst first:
 *  - `meaning`: a meaning check or the dialogue guard failed, or the answer was declined, cut off, off schema or restructured.
 *    Never kept; reported for failed units.
 *  - `cosmetic`: the only failed meaning check is the word ratio (the deferred `permissive` tier would keep it).
 *  - `signal-negative`: the signal score S is at most 0 (the net removal, under the `events` rule), or a channel outside the
 *    style channels rose.
 *  - `style`: otherwise; at most the style channels (RH-01, RH-02, PT-02, ST-03, PROC-06, PROC-07) rose.
 */
import type { ChannelId } from '../channels/types.ts';
import type { AttemptEval } from './evaluate2.ts';
import { TOL } from './floors.ts';
import type { MeaningFailure } from './meaning2.ts';
import { S_PASS } from './signal-score.ts';
import type { Unit } from './units.ts';

export type Severity = 'cosmetic' | 'style' | 'signal-negative' | 'meaning';

/** Worst first: the lens sorts and filters by this order. */
export const SEVERITIES: readonly Severity[] = ['meaning', 'signal-negative', 'style', 'cosmetic'];

/** Channels whose rise in a rewrite is a matter of style (BENCH-V3-PLAN §7.2): the operator may well accept it. */
export const STYLE_CHANNELS: ReadonlySet<ChannelId> = new Set<ChannelId>([
	'RH-01',
	'RH-02',
	'PT-02',
	'ST-03',
	'PROC-06',
	'PROC-07',
]);

/** Why an answer has no measurement. */
export type ReportProblem = 'declined' | 'truncated' | 'schema' | 'structure';

export interface FailureReport {
	severity: Severity;
	/** Targeted channels: the outcome, and for evidence spans how many are gone. */
	targeted: Array<{
		channel: ChannelId;
		status: 'addressed' | 'partial' | 'none';
		gone?: number;
		of?: number;
	}>;
	/** The signal score with its parts (points removed, added, carried); under the `events` rule `net` stands in. */
	signal?: {
		rule: 'S' | 'events' | 'legacy';
		S?: number;
		R?: number;
		I?: number;
		B?: number;
		net?: number;
	};
	/** Document-wide rises past the channel's margin (R3), with the phrase that did it when one is known. */
	document: Array<{ channel: ChannelId; points: number; margin: number; phrase?: string }>;
	/** Window issues (R2): a rise next to the unit, or across the chunk's edge. */
	window: Array<{
		channel: ChannelId;
		/** 1-based paragraph. */
		para: number;
		spans: string[];
		cross?: true;
		segment?: true;
	}>;
	/** Events the rewrite introduced (or, for a channel read the other way, lost). */
	introduced: Array<{ channel: ChannelId; spans: string[]; lost?: true }>;
	/** The failed meaning checks' and the guard's sentences, and the problem sentence of an answer with no measurement. */
	checks: string[];
	summary: string;
}

export interface ReportInput {
	unit: Pick<Unit, 'channels' | 'evidence'>;
	/** The measurement of the chosen version; absent when the answer has none. */
	ev?: AttemptEval | undefined;
	meaning?: readonly MeaningFailure[];
	guard?: readonly string[];
	problem?: ReportProblem;
	/** The sentence to show for a problem (the loop's reason). */
	sentence?: string;
}

const MAX_ITEMS = 3;
const MAX_SPANS = 2;
const MAX_SPAN_CHARS = 60;
const MAX_WORDS = 40;

const r2 = (x: number): number => Math.round(x * 100) / 100;
const cut = (s: string): string =>
	s.length > MAX_SPAN_CHARS ? `${s.slice(0, MAX_SPAN_CHARS - 1)}…` : s;
const spansOf = (xs: readonly string[]): string[] => xs.slice(0, MAX_SPANS).map(cut);
const signed = (x: number): string => {
	const v = r2(x);
	return `${v < 0 ? '−' : v > 0 ? '+' : ''}${Math.abs(v).toFixed(2)}`;
};
/** A check sentence can carry the judge's free text: cut it to 200 characters. */
const cutCheck = (s: string): string => (s.length > 200 ? `${s.slice(0, 199)}…` : s);
const list = (xs: readonly string[]): string => xs.join(', ');

/** The severity of a failed or kept unit (pure; the report's rule). */
export function severityOf(x: {
	meaning?: readonly MeaningFailure[];
	guard?: readonly string[];
	problem?: ReportProblem;
	rises: readonly ChannelId[];
	/** S under the `S` rule, else the net removal; undefined when nothing was measured. */
	score?: number;
}): Severity {
	if (x.problem || (x.guard?.length ?? 0) > 0) return 'meaning';
	const failed = x.meaning ?? [];
	if (failed.length > 0)
		return failed.every((f) => f.check === 'word-ratio') ? 'cosmetic' : 'meaning';
	if ((x.score ?? 1) <= TOL) return 'signal-negative';
	return x.rises.every((c) => STYLE_CHANNELS.has(c)) ? 'style' : 'signal-negative';
}

/** The channels the measurement says rose: R3's rises, R1's introduced events, R2's issues (segment values excluded), the translation guard's failures. */
function risers(ev: AttemptEval): ChannelId[] {
	const out = new Set<ChannelId>();
	for (const r of ev.r3.rises) out.add(r.channel);
	for (const i of ev.r1.introduced) out.add(i.channel);
	for (const i of ev.r1.lost) out.add(i.channel);
	for (const i of ev.r2.issues) if (!i.segment) out.add(i.channel);
	// the translation guard (ED-162): an introduced strong TR-01 marker, a lost strong TR-02 marker
	if (ev.tr && !ev.tr.ok) {
		if (ev.tr.introduced.length > 0 || ev.tr.rate.some((r) => !r.ok && r.side === 'machine'))
			out.add('TR-01');
		if (ev.tr.lost.length > 0 || ev.tr.rate.some((r) => !r.ok && r.side === 'human'))
			out.add('TR-02');
	}
	return [...out];
}

const clip = (s: string): string => {
	const w = s.split(/\s+/).filter(Boolean);
	return w.length <= MAX_WORDS ? w.join(' ') : `${w.slice(0, MAX_WORDS).join(' ')}…`;
};

/** The one-sentence summary, from a template (at most 40 words). */
function summarise(r: Omit<FailureReport, 'summary'>, input: ReportInput): string {
	if (input.problem) return clip(input.sentence ?? PROBLEM[input.problem]);
	const meaning = input.meaning?.[0]?.text ?? input.guard?.[0];
	const addressed = r.targeted.filter((t) => t.status === 'addressed').map((t) => t.channel);
	const partial = r.targeted.filter((t) => t.status === 'partial').map((t) => t.channel);
	const where = input.unit.channels.length > 0 ? ` in ${list(input.unit.channels)}` : '';
	// an answer that failed a check before it was measured says nothing about the pattern
	const lead = !input.ev
		? 'The rewrite was not measured'
		: addressed.length > 0
			? `Removed the pattern in ${list(addressed)}`
			: partial.length > 0
				? `Partly removed the pattern in ${list(partial)}`
				: `Did not remove the pattern${where}`;
	if (meaning) return clip(`${lead}, but the check failed: ${meaning}`);
	const rise = r.document[0];
	if (rise)
		return clip(
			`${lead}, but made the document's ${rise.channel} ${r2(rise.points).toFixed(2)} points more machine-like (margin ${r2(rise.margin).toFixed(2)})${rise.phrase ? ` by using “${rise.phrase}”` : ''}.`
		);
	const sc = r.signal;
	// the same score the severity judges: S under the `S` rule, the net removal otherwise
	if (sc?.rule === 'S' && sc.S !== undefined && sc.S <= TOL)
		return clip(`${lead}, but the net signal score is ${signed(sc.S)}.`);
	if (sc && sc.rule !== 'S' && sc.net !== undefined && sc.net <= TOL)
		return clip(`${lead}, but the net removal is ${signed(sc.net)}.`);
	const intro = r.introduced[0];
	if (intro)
		return clip(
			`${lead}, but ${intro.lost ? 'lost' : 'introduced'} ${intro.channel}${intro.spans[0] ? ` “${intro.spans[0]}”` : ''}.`
		);
	const win = r.window.find((w) => !w.segment) ?? r.window[0];
	if (win) return clip(`${lead}, but ${win.channel} rose near ¶${win.para}.`);
	if (sc?.rule === 'S' && sc.S !== undefined && sc.S < S_PASS)
		return clip(`${lead}, but the net signal score ${signed(sc.S)} is below ${S_PASS.toFixed(2)}.`);
	return clip(`${lead}.`);
}

const PROBLEM: Record<ReportProblem, string> = {
	declined: 'The model declined this passage.',
	truncated: 'The answer was cut off at the output limit.',
	schema: 'The answer did not match the requested format.',
	structure: 'The rewrite changed the paragraph structure.',
};

/** The failure report of one version of one unit. */
export function buildFailureReport(input: ReportInput): FailureReport {
	const { ev, unit } = input;
	const checks = [
		...(input.meaning ?? []).map((m) => m.text),
		...(input.guard ?? []),
		...(input.problem && input.sentence ? [input.sentence] : []),
	]
		.slice(0, MAX_ITEMS)
		.map(cutCheck);

	const targeted: FailureReport['targeted'] = unit.channels.map((channel) => {
		const o = ev?.outcomes.find((x) => x.channel === channel);
		if (!o) return { channel, status: 'none' };
		const of = unit.evidence.filter((e) => e.channel === channel && e.weight > 0).length;
		return o.kind === 'events' && of > 0
			? { channel, status: o.status, gone: Math.max(0, of - o.surviving.length), of }
			: { channel, status: o.status };
	});

	const phraseOf = (c: ChannelId): string | undefined => {
		const fromIntro = ev?.r1.introduced.find((i) => i.channel === c)?.spans[0];
		if (fromIntro) return cut(fromIntro);
		const issue = ev?.r2.issues.find((i) => i.channel === c && i.spans.length > 0);
		return issue?.spans[0] ? cut(issue.spans[0]) : undefined;
	};
	const document: FailureReport['document'] = (ev?.r3.rises ?? [])
		.slice()
		.sort((a, b) => b.delta - a.delta)
		.slice(0, MAX_ITEMS)
		.map((r) => {
			const phrase = phraseOf(r.channel);
			return {
				channel: r.channel,
				points: r2(r.delta),
				margin: r2(r.floor),
				...(phrase ? { phrase } : {}),
			};
		});
	const window: FailureReport['window'] = (ev?.r2.issues ?? [])
		.slice()
		.sort((a, b) => Number(!!a.segment) - Number(!!b.segment) || b.delta - a.delta)
		.slice(0, MAX_ITEMS)
		.map((i) => ({
			channel: i.channel,
			para: i.para + 1,
			spans: spansOf(i.spans),
			...(i.crossBoundary ? { cross: true as const } : {}),
			...(i.segment ? { segment: true as const } : {}),
		}));
	const introduced: FailureReport['introduced'] = [
		...(ev?.r1.introduced ?? []).map((i) => ({ channel: i.channel, spans: spansOf(i.spans) })),
		...(ev?.r1.lost ?? []).map((i) => ({
			channel: i.channel,
			spans: spansOf(i.spans),
			lost: true as const,
		})),
		...(ev?.tr && ev.tr.introduced.length > 0
			? [{ channel: 'TR-01' as ChannelId, spans: spansOf(ev.tr.introduced) }]
			: []),
		...(ev?.tr && ev.tr.lost.length > 0
			? [{ channel: 'TR-02' as ChannelId, spans: spansOf(ev.tr.lost), lost: true as const }]
			: []),
	].slice(0, MAX_ITEMS);

	const sig = ev?.signal;
	const signal: FailureReport['signal'] | undefined = ev
		? sig && ev.rule === 'S'
			? { rule: 'S', S: r2(sig.S), R: r2(sig.R), I: r2(sig.I), B: r2(sig.B) }
			: { rule: ev.rule, net: r2(ev.net), ...(sig ? { S: r2(sig.S), B: r2(sig.B) } : {}) }
		: undefined;

	const severity = severityOf({
		...(input.meaning ? { meaning: input.meaning } : {}),
		...(input.guard ? { guard: input.guard } : {}),
		...(input.problem ? { problem: input.problem } : {}),
		rises: ev ? risers(ev) : [],
		...(ev ? { score: ev.rule === 'S' ? (ev.signal?.S ?? 0) : ev.net } : {}),
	});

	const body: Omit<FailureReport, 'summary'> = {
		severity,
		targeted,
		...(signal ? { signal } : {}),
		document,
		window,
		introduced,
		checks,
	};
	return { ...body, summary: summarise(body, input) };
}
